import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserCheck, Users, ArrowRightLeft, Search, Save, RefreshCw,
  Calendar, X, Upload, CheckCircle, FileSpreadsheet, Plus, Trash2, Edit3,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Lock, Unlock, ShieldCheck,
  FileText, Download, AlertCircle
} from 'lucide-react';
import {
  getEmployees, getEmployee,
  getAttendance, getAttendanceSummary, bulkMarkAttendance,
  getAttendanceConfirmationStatus, confirmAttendance, unconfirmAttendance,
  getTransfers, createTransfer, getDepartments,
  uploadEmployeesPreview, uploadEmployeesConfirm,
  createEmployee, updateEmployee, deleteEmployee,
  getExplanations, requestExplanation, reviewExplanation, downloadExplanation,
} from '../services/api';

// ─── Константы ──────────────────────────────────────────────────────

const ATTENDANCE_STATUSES = [
  { value: 'present', label_ru: 'На работе', label_uz: 'Ishda', color: '#22c55e', bg: '#f0fdf4' },
  { value: 'late_excused', label_ru: 'Опоздал (уважит.)', label_uz: 'Kechikdi (uzrli)', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'late_unexcused', label_ru: 'Опоздал (неуважит.)', label_uz: 'Kechikdi (uzrsiz)', color: '#ef4444', bg: '#fef2f2' },
  { value: 'on_errand', label_ru: 'Отпросился на время', label_uz: 'Ruxsat olib ketdi', color: '#8b5cf6', bg: '#f5f3ff' },
  { value: 'vacation', label_ru: 'В отпуске', label_uz: "Ta'tilda", color: '#3b82f6', bg: '#eff6ff' },
  { value: 'sick_leave', label_ru: 'На больничном', label_uz: 'Kasallik varaqasida', color: '#f59e0b', bg: '#fffbeb' },
  { value: 'maternity', label_ru: 'Уход за ребёнком', label_uz: 'Bola parvarishida', color: '#8b5cf6', bg: '#f5f3ff' },
  { value: 'pregnancy_leave', label_ru: 'Декретный отпуск', label_uz: "Homiladorlik ta'tilida", color: '#ec4899', bg: '#fdf2f8' },
  { value: 'business_trip', label_ru: 'В командировке', label_uz: 'Xizmat safarida', color: '#06b6d4', bg: '#ecfeff' },
  { value: 'absent', label_ru: 'Отсутствует', label_uz: 'Kelmagan', color: '#ef4444', bg: '#fef2f2' },
  { value: 'not_reported', label_ru: 'Не отмечен', label_uz: 'Belgilanmagan', color: '#94a3b8', bg: '#f1f5f9' },
];

const TAB_KEYS = ['attendance', 'employees', 'transfers', 'explanations'];

function formatDate(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('ru-RU');
}

function formatDateISO(d) {
  if (!d) return '';
  if (typeof d === 'string') return d;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function todayISO() {
  return formatDateISO(new Date());
}

/** Генерирует массив номеров страниц с «...» */
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, 2, total - 1, total]);
  for (let d = -1; d <= 1; d++) {
    const p = current + d;
    if (p >= 1 && p <= total) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
}

// ─── Стили ──────────────────────────────────────────────────────────

const s = {
  page: { padding: '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '10px' },
  subtitle: { fontSize: '14px', color: '#64748b', margin: 0 },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', backgroundColor: '#f1f5f9', borderRadius: '12px', padding: '4px' },
  tab: { flex: 1, padding: '10px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#64748b', backgroundColor: 'transparent', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  tabActive: { backgroundColor: '#fff', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  card: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' },
  summaryRow: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  summaryCard: { flex: '1', minWidth: '120px', padding: '14px', borderRadius: '10px', textAlign: 'center' },
  summaryValue: { fontSize: '24px', fontWeight: '700', margin: '0 0 4px' },
  summaryLabel: { fontSize: '12px', fontWeight: '500', margin: 0 },
  controlRow: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  select: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e293b', backgroundColor: '#fff', outline: 'none', minWidth: '180px' },
  input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e293b', outline: 'none', flex: 1, minWidth: '200px' },
  dateInput: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', color: '#1e293b', outline: 'none' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' },
  td: { padding: '10px 12px', fontSize: '14px', color: '#334155', borderBottom: '1px solid #f1f5f9' },
  badge: (color, bg) => ({ display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', color, backgroundColor: bg }),
  btn: { padding: '8px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s' },
  btnPrimary: { backgroundColor: '#3b82f6', color: '#fff' },
  btnSuccess: { backgroundColor: '#22c55e', color: '#fff' },
  btnSecondary: { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #d1d5db' },
  loading: { textAlign: 'center', padding: '40px', color: '#64748b', fontSize: '14px' },
  empty: { textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '14px' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '90%', maxHeight: '80vh', overflowY: 'auto' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '4px' },
  statusSelect: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', color: '#1e293b', outline: 'none', width: '100%' },
};

// ─── Компонент ──────────────────────────────────────────────────────

function HRDashboard({ embedded = false }) {
  const { i18n } = useTranslation(['hr', 'common']);
  const isRu = i18n.language === 'ru';

  const [activeTab, setActiveTab] = useState('attendance');
  const [departments, setDepartments] = useState([]);

  // === Attendance state ===
  const [attDate, setAttDate] = useState(todayISO());
  const [attDeptFilter, setAttDeptFilter] = useState('');
  const [employees, setEmployees] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({}); // employee_id -> { status, note }
  const [summary, setSummary] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [attSaving, setAttSaving] = useState(false);
  const [attMessage, setAttMessage] = useState('');
  // Подтверждения HR: { department_id -> true/false }
  const [confirmations, setConfirmations] = useState({});
  const [confirmLoading, setConfirmLoading] = useState(false);

  // === Employees state ===
  const [empList, setEmpList] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [empDeptFilter, setEmpDeptFilter] = useState('');
  const [empRoleFilter, setEmpRoleFilter] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [empPage, setEmpPage] = useState(1);
  const [empPageSize, setEmpPageSize] = useState(20);

  // === Explanations state ===
  const [explanations, setExplanations] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expFilter, setExpFilter] = useState(''); // '', 'requested', 'submitted', 'reviewed'
  const [expReviewNote, setExpReviewNote] = useState('');
  const [expReviewing, setExpReviewing] = useState(null); // id объяснительной

  // === Transfers state ===
  const [transfers, setTransfers] = useState([]);
  const [trLoading, setTrLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({ employee: '', to_department: '', transfer_date: todayISO(), reason: '', order_number: '' });
  const [trSaving, setTrSaving] = useState(false);

  // === Upload state ===
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadStep, setUploadStep] = useState('select'); // 'select' | 'preview' | 'result'
  const [uploadOptions, setUploadOptions] = useState({
    default_password: 'password123',
    default_role: 'employee',
    deactivate_test: false,
    skip_duplicates: true,
  });
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadPage, setUploadPage] = useState(1);

  // === Employee CRUD state ===
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null); // null = create, object = edit
  const [empForm, setEmpForm] = useState({
    last_name: '', first_name: '', middle_name: '',
    department: '', position: '', role: 'employee',
    gender: '', birth_date: '', hire_date: '', email: '', phone: '',
    password: 'password123',
  });
  const [empSaving, setEmpSaving] = useState(false);

  // Load departments once
  useEffect(() => {
    getDepartments().then(res => {
      setDepartments(res.data.results || res.data || []);
    }).catch(() => {});
  }, []);

  // ─── Attendance logic ──────────────────────────────────────────────

  const loadAttendance = useCallback(async () => {
    setAttLoading(true);
    setAttMessage('');
    try {
      // Load all employees (for the department filter)
      const params = { is_active: true };
      if (attDeptFilter) params.department = attDeptFilter;
      const empRes = await getEmployees(params);
      const emps = empRes.data.results || empRes.data || [];
      setEmployees(emps);

      // Load existing attendance records for the date
      const attParams = { date: attDate };
      if (attDeptFilter) attParams.department = attDeptFilter;
      const attRes = await getAttendance(attParams);
      const records = attRes.data.results || attRes.data || [];
      const map = {};
      records.forEach(r => {
        map[r.employee] = { id: r.id, status: r.status, note: r.note || '' };
      });
      // Для неотмеченных сотрудников — статус "not_reported"
      emps.forEach(emp => {
        if (!map[emp.id]) {
          map[emp.id] = { status: 'not_reported', note: '' };
        }
      });
      setAttendanceMap(map);

      // Load summary
      const sumRes = await getAttendanceSummary({ date: attDate });
      setSummary(sumRes.data);

      // Загружаем статусы подтверждений HR за эту дату
      const confRes = await getAttendanceConfirmationStatus({ date: attDate });
      const confMap = {};
      (confRes.data.confirmed_departments || []).forEach(c => {
        confMap[c.department_id] = c;
      });
      setConfirmations(confMap);
    } catch (err) {
      console.error('Error loading attendance:', err);
    }
    setAttLoading(false);
  }, [attDate, attDeptFilter]);

  useEffect(() => {
    if (activeTab === 'attendance') loadAttendance();
  }, [activeTab, loadAttendance]);

  const handleStatusChange = (empId, newStatus) => {
    setAttendanceMap(prev => ({ ...prev, [empId]: { ...prev[empId], status: newStatus } }));
  };

  const handleNoteChange = (empId, note) => {
    setAttendanceMap(prev => ({ ...prev, [empId]: { ...prev[empId], note } }));
  };

  const saveAttendance = async () => {
    setAttSaving(true);
    setAttMessage('');
    try {
      const records = Object.entries(attendanceMap)
        .filter(([, data]) => data.status !== 'not_reported')
        .map(([empId, data]) => ({
          employee_id: parseInt(empId),
          status: data.status,
          note: data.note || '',
        }));
      const res = await bulkMarkAttendance({ date: attDate, records });
      setAttMessage(
        isRu
          ? `Сохранено: ${res.data.created} создано, ${res.data.updated} обновлено`
          : `Saqlandi: ${res.data.created} yaratildi, ${res.data.updated} yangilandi`
      );
      // Reload summary
      const sumRes = await getAttendanceSummary({ date: attDate });
      setSummary(sumRes.data);
    } catch (err) {
      setAttMessage(isRu ? 'Ошибка сохранения' : 'Saqlashda xatolik');
      console.error(err);
    }
    setAttSaving(false);
  };

  // Подтверждение посещаемости отдела за текущую дату
  const handleConfirm = async (deptId) => {
    const msg = isRu
      ? `Подтвердить посещаемость отдела за ${attDate}? После подтверждения отдел не сможет редактировать.`
      : `${attDate} sanasidagi bo'lim davomatini tasdiqlaysizmi? Tasdiqdan so'ng bo'lim o'zgartira olmaydi.`;
    if (!window.confirm(msg)) return;
    setConfirmLoading(true);
    try {
      await confirmAttendance({ date: attDate, department_id: deptId });
      const confRes = await getAttendanceConfirmationStatus({ date: attDate });
      const confMap = {};
      (confRes.data.confirmed_departments || []).forEach(c => { confMap[c.department_id] = c; });
      setConfirmations(confMap);
    } catch (err) {
      alert(isRu ? 'Ошибка подтверждения' : 'Tasdiqlashda xatolik');
    }
    setConfirmLoading(false);
  };

  const handleUnconfirm = async (deptId) => {
    const msg = isRu ? 'Снять подтверждение? Отдел снова сможет редактировать посещаемость.' : 'Tasdiqlashni bekor qilish? Bo\'lim yana o\'zgartira oladi.';
    if (!window.confirm(msg)) return;
    setConfirmLoading(true);
    try {
      await unconfirmAttendance({ date: attDate, department_id: deptId });
      setConfirmations(prev => { const n = {...prev}; delete n[deptId]; return n; });
    } catch (err) {
      alert(isRu ? 'Ошибка' : 'Xatolik');
    }
    setConfirmLoading(false);
  };

  const getStatusInfo = (statusValue) => {
    return ATTENDANCE_STATUSES.find(s => s.value === statusValue) || ATTENDANCE_STATUSES[0];
  };

  // ─── Employees logic ───────────────────────────────────────────────

  const loadEmployees = useCallback(async () => {
    setEmpLoading(true);
    setEmpPage(1);
    try {
      const params = {};
      if (empSearch) params.search = empSearch;
      if (empDeptFilter) params.department = empDeptFilter;
      if (empRoleFilter) params.role = empRoleFilter;
      const res = await getEmployees(params);
      setEmpList(res.data.results || res.data || []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
    setEmpLoading(false);
  }, [empSearch, empDeptFilter, empRoleFilter]);

  useEffect(() => {
    if (activeTab === 'employees') {
      const timer = setTimeout(loadEmployees, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTab, loadEmployees]);

  const openEmployeeDetail = async (id) => {
    try {
      const res = await getEmployee(id);
      setSelectedEmployee(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ─── Transfers logic ───────────────────────────────────────────────

  const loadTransfers = useCallback(async () => {
    setTrLoading(true);
    try {
      const res = await getTransfers();
      setTransfers(res.data.results || res.data || []);
    } catch (err) {
      console.error('Error loading transfers:', err);
    }
    setTrLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'transfers') loadTransfers();
  }, [activeTab, loadTransfers]);

  // ─── Explanations logic ───────────────────────────────────────────

  const loadExplanations = useCallback(async () => {
    setExpLoading(true);
    try {
      const params = {};
      if (expFilter) params.status = expFilter;
      const res = await getExplanations(params);
      setExplanations(res.data.results || res.data || []);
    } catch (err) {
      console.error('Error loading explanations:', err);
    }
    setExpLoading(false);
  }, [expFilter]);

  useEffect(() => {
    if (activeTab === 'explanations') loadExplanations();
  }, [activeTab, loadExplanations]);

  const handleRequestExplanation = async (employee, date, attendanceId) => {
    try {
      await requestExplanation({ employee_id: employee.id, date, attendance_id: attendanceId });
      alert(isRu
        ? `Объяснительная запрошена у ${employee.employee_name || employee.full_name}`
        : `Tushuntirish so'raldi`);
      loadAttendance();
    } catch (err) {
      alert(isRu ? 'Ошибка запроса' : 'Xatolik');
    }
  };

  const handleDownloadExplanation = async (exp) => {
    try {
      const res = await downloadExplanation(exp.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `explanatory_${exp.employee_name}_${exp.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(isRu ? 'Ошибка скачивания файла' : 'Faylni yuklab olishda xatolik');
    }
  };

  const handleReviewExplanation = async (expId) => {
    try {
      await reviewExplanation(expId, expReviewNote);
      setExpReviewing(null);
      setExpReviewNote('');
      loadExplanations();
    } catch (err) {
      alert(isRu ? 'Ошибка рассмотрения' : 'Xatolik');
    }
  };

  // ─── Upload handlers ──────────────────────────────────────────────

  const handleUploadPreview = async () => {
    if (!uploadFile) return;
    setUploadLoading(true);
    try {
      const res = await uploadEmployeesPreview(uploadFile);
      setUploadPreview(res.data);
      setUploadPage(1);
      setUploadStep('preview');
    } catch (err) {
      const msg = err.response?.data?.detail || (isRu ? 'Ошибка загрузки файла' : 'Faylni yuklashda xatolik');
      alert(msg);
      console.error(err);
    }
    setUploadLoading(false);
  };

  const handleUploadConfirm = async () => {
    if (!uploadFile) {
      alert(isRu ? 'Файл не выбран. Вернитесь на шаг 1 и выберите файл.' : 'Fayl tanlanmagan.');
      return;
    }
    setUploadLoading(true);
    try {
      const res = await uploadEmployeesConfirm(uploadFile, uploadOptions);
      setUploadResult(res.data);
      setUploadStep('result');
    } catch (err) {
      const msg = err.response?.data?.detail || (isRu ? 'Ошибка импорта' : 'Import xatoligi');
      alert(msg);
      console.error(err);
    }
    setUploadLoading(false);
  };

  const openEmpCreate = () => {
    setEditingEmp(null);
    setEmpForm({
      last_name: '', first_name: '', middle_name: '',
      department: '', position: '', role: 'employee',
      gender: '', birth_date: '', hire_date: '', email: '', phone: '',
      password: 'password123',
    });
    setShowEmpForm(true);
  };

  const openEmpEdit = (emp) => {
    setEditingEmp(emp);
    setEmpForm({
      last_name: emp.last_name || '', first_name: emp.first_name || '', middle_name: emp.middle_name || '',
      department: emp.department || '', position: emp.position || '', role: emp.role || 'employee',
      gender: emp.gender || '', birth_date: emp.birth_date || '', hire_date: emp.hire_date || '',
      email: emp.email || '', phone: emp.phone || '', password: '',
    });
    setShowEmpForm(true);
  };

  const handleSaveEmployee = async () => {
    if (!empForm.last_name || !empForm.first_name) {
      alert(isRu ? 'Фамилия и имя обязательны' : 'Familiya va ism majburiy');
      return;
    }
    setEmpSaving(true);
    try {
      const data = { ...empForm };
      // Убираем пустые поля
      Object.keys(data).forEach(k => { if (data[k] === '') delete data[k]; });
      if (editingEmp) {
        delete data.password;
        await updateEmployee(editingEmp.id, data);
      } else {
        await createEmployee(data);
      }
      setShowEmpForm(false);
      loadEmployees();
    } catch (err) {
      const detail = err.response?.data?.detail || JSON.stringify(err.response?.data || '');
      alert((isRu ? 'Ошибка: ' : 'Xatolik: ') + detail);
    }
    setEmpSaving(false);
  };

  const handleDeleteEmployee = async (emp) => {
    const msg = isRu
      ? `Деактивировать сотрудника ${emp.full_name}?`
      : `${emp.full_name} xodimni o'chirish?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteEmployee(emp.id);
      loadEmployees();
    } catch (err) {
      alert(isRu ? 'Ошибка удаления' : 'O\'chirishda xatolik');
    }
  };

  const handleCreateTransfer = async () => {
    setTrSaving(true);
    try {
      await createTransfer(transferForm);
      setShowTransferModal(false);
      setTransferForm({ employee: '', to_department: '', transfer_date: todayISO(), reason: '', order_number: '' });
      loadTransfers();
    } catch (err) {
      alert(isRu ? 'Ошибка создания перевода' : 'Xatolik');
      console.error(err);
    }
    setTrSaving(false);
  };

  // ─── Render ────────────────────────────────────────────────────────

  const tabIcons = { attendance: Calendar, employees: Users, transfers: ArrowRightLeft, explanations: FileText };
  const tabLabels = {
    attendance: isRu ? 'Посещаемость' : 'Davomat',
    employees: isRu ? 'Сотрудники' : 'Xodimlar',
    transfers: isRu ? 'Переводы' : "Ko'chirishlar",
    explanations: isRu ? 'Объяснительные' : 'Tushuntirish xatlari',
  };

  return (
    <div style={embedded ? {} : s.page}>
      {/* Header — скрываем при встраивании */}
      {!embedded && (
        <div style={s.header}>
          <h1 style={s.title}><UserCheck size={24} /> {isRu ? 'Отдел кадров' : 'Kadrlar bo\'limi'}</h1>
          <p style={s.subtitle}>{isRu ? 'Управление сотрудниками и мониторинг посещаемости' : 'Xodimlarni boshqarish va davomatni nazorat qilish'}</p>
        </div>
      )}

      {/* Заголовок при встраивании */}
      {embedded && (
        <h2 className="section-title" style={{ margin: '0 0 1rem', fontSize: '1.1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1e293b' }}>
          <UserCheck size={20} style={{ color: '#3b82f6' }} />
          {isRu ? 'Кадровая работа' : 'Kadrlar ishi'}
        </h2>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        {TAB_KEYS.map(key => {
          const Icon = tabIcons[key];
          return (
            <button
              key={key}
              style={{ ...s.tab, ...(activeTab === key ? s.tabActive : {}) }}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={16} /> {tabLabels[key]}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'attendance' && renderAttendance()}
      {activeTab === 'employees' && renderEmployees()}
      {activeTab === 'transfers' && renderTransfers()}
      {activeTab === 'explanations' && renderExplanations()}

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div style={s.modal} onClick={() => setSelectedEmployee(null)}>
          <div style={s.modalContent} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>{selectedEmployee.full_name}</span>
              <button style={{ ...s.btn, ...s.btnSecondary, padding: '4px 8px' }} onClick={() => setSelectedEmployee(null)}>
                <X size={16} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>{isRu ? 'Должность' : 'Lavozim'}</span><br /><strong>{selectedEmployee.position || '—'}</strong></div>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>{isRu ? 'Отдел' : "Bo'lim"}</span><br /><strong>{isRu ? selectedEmployee.department_name : selectedEmployee.department_name_uz || selectedEmployee.department_name}</strong></div>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>{isRu ? 'Дата приёма' : 'Qabul sanasi'}</span><br /><strong>{formatDate(selectedEmployee.hire_date) || '—'}</strong></div>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>{isRu ? 'Дата рождения' : "Tug'ilgan sana"}</span><br /><strong>{formatDate(selectedEmployee.birth_date) || '—'}</strong></div>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>{isRu ? 'Телефон' : 'Telefon'}</span><br /><strong>{selectedEmployee.phone || '—'}</strong></div>
              <div><span style={{ color: '#64748b', fontSize: '12px' }}>Email</span><br /><strong>{selectedEmployee.email || '—'}</strong></div>
            </div>

            {/* Transfer History */}
            {selectedEmployee.transfers && selectedEmployee.transfers.length > 0 && (
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: '16px 0 8px' }}>
                  {isRu ? 'История переводов' : "Ko'chirishlar tarixi"}
                </h4>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>{isRu ? 'Дата' : 'Sana'}</th>
                      <th style={s.th}>{isRu ? 'Из' : 'Dan'}</th>
                      <th style={s.th}>{isRu ? 'В' : 'Ga'}</th>
                      <th style={s.th}>{isRu ? 'Причина' : 'Sabab'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEmployee.transfers.map(tr => (
                      <tr key={tr.id}>
                        <td style={s.td}>{formatDate(tr.transfer_date)}</td>
                        <td style={s.td}>{tr.from_department_name || '—'}</td>
                        <td style={s.td}>{tr.to_department_name}</td>
                        <td style={s.td}>{tr.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transfer Create Modal */}
      {showTransferModal && (
        <div style={s.modal} onClick={() => setShowTransferModal(false)}>
          <div style={s.modalContent} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span>{isRu ? 'Оформить перевод' : "Ko'chirishni rasmiylashtirish"}</span>
              <button style={{ ...s.btn, ...s.btnSecondary, padding: '4px 8px' }} onClick={() => setShowTransferModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>{isRu ? 'Сотрудник' : 'Xodim'}</label>
              <select style={s.select} value={transferForm.employee} onChange={e => setTransferForm(p => ({ ...p, employee: e.target.value }))}>
                <option value="">{isRu ? '-- Выберите --' : '-- Tanlang --'}</option>
                {empList.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.full_name} ({isRu ? emp.department_name : emp.department_name_uz})</option>
                ))}
              </select>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>{isRu ? 'В отдел' : "Qaysi bo'limga"}</label>
              <select style={s.select} value={transferForm.to_department} onChange={e => setTransferForm(p => ({ ...p, to_department: e.target.value }))}>
                <option value="">{isRu ? '-- Выберите --' : '-- Tanlang --'}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{isRu ? d.name_ru : d.name_uz}</option>
                ))}
              </select>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>{isRu ? 'Дата перевода' : "Ko'chirish sanasi"}</label>
              <input type="date" style={s.dateInput} value={transferForm.transfer_date} onChange={e => setTransferForm(p => ({ ...p, transfer_date: e.target.value }))} />
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>{isRu ? 'Причина' : 'Sabab'}</label>
              <input style={s.input} value={transferForm.reason} onChange={e => setTransferForm(p => ({ ...p, reason: e.target.value }))} />
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>{isRu ? 'Номер приказа' : 'Buyruq raqami'}</label>
              <input style={s.input} value={transferForm.order_number} onChange={e => setTransferForm(p => ({ ...p, order_number: e.target.value }))} />
            </div>

            <button
              style={{ ...s.btn, ...s.btnPrimary, width: '100%', justifyContent: 'center' }}
              onClick={handleCreateTransfer}
              disabled={trSaving || !transferForm.employee || !transferForm.to_department}
            >
              {trSaving ? <RefreshCw size={16} className="spin" /> : <Save size={16} />}
              {isRu ? 'Сохранить' : 'Saqlash'}
            </button>
          </div>
        </div>
      )}

      {/* Excel Upload Modal */}
      {showUploadModal && (
        <div style={s.modal} onClick={() => setShowUploadModal(false)}>
          <div style={{ ...s.modalContent, maxWidth: uploadStep === 'preview' ? '900px' : '500px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileSpreadsheet size={20} color="#22c55e" />
                {isRu ? 'Импорт сотрудников из Excel' : 'Xodimlarni Exceldan import qilish'}
              </span>
              <button style={{ ...s.btn, ...s.btnSecondary, padding: '4px 8px' }} onClick={() => setShowUploadModal(false)}>
                <X size={16} />
              </button>
            </div>

            {/* Step 1: File select */}
            {uploadStep === 'select' && (
              <div>
                <div style={s.formGroup}>
                  <label style={s.label}>{isRu ? 'Файл Excel (.xlsx)' : 'Excel fayli (.xlsx)'}</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={e => setUploadFile(e.target.files[0] || null)}
                    style={{ ...s.input, padding: '10px', border: '2px dashed #d1d5db', cursor: 'pointer' }}
                  />
                </div>

                <div style={{ ...s.formGroup, padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                  <label style={{ ...s.label, marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>
                    {isRu ? 'Настройки импорта' : 'Import sozlamalari'}
                  </label>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={s.label}>{isRu ? 'Пароль по умолчанию' : 'Standart parol'}</label>
                      <input
                        style={s.input}
                        type="text"
                        value={uploadOptions.default_password}
                        onChange={e => setUploadOptions(p => ({ ...p, default_password: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={s.label}>{isRu ? 'Роль' : 'Rol'}</label>
                      <select
                        style={s.select}
                        value={uploadOptions.default_role}
                        onChange={e => setUploadOptions(p => ({ ...p, default_role: e.target.value }))}
                      >
                        <option value="employee">{isRu ? 'Сотрудник' : 'Xodim'}</option>
                        <option value="department_head">{isRu ? 'Нач. отдела' : "Bo'lim boshlig'i"}</option>
                        <option value="director">{isRu ? 'Директор' : 'Direktor'}</option>
                        <option value="deputy_director">{isRu ? 'Зам. директора' : 'Direktor o\'rinbosari'}</option>
                        <option value="analyst">{isRu ? 'Аналитик' : 'Tahlilchi'}</option>
                      </select>
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#475569' }}>
                    <input
                      type="checkbox"
                      checked={uploadOptions.deactivate_test}
                      onChange={e => setUploadOptions(p => ({ ...p, deactivate_test: e.target.checked }))}
                    />
                    {isRu ? 'Деактивировать тестовых сотрудников перед импортом' : 'Importdan oldin test xodimlarni o\'chirish'}
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#475569', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={uploadOptions.skip_duplicates}
                      onChange={e => setUploadOptions(p => ({ ...p, skip_duplicates: e.target.checked }))}
                    />
                    {isRu ? 'Пропускать дубликаты (совпадение ФИО + дата рождения)' : "Dublikatlarni o'tkazib yuborish (F.I.O. + tug'ilgan sana)"}
                  </label>
                </div>

                <div style={{ marginTop: '8px', padding: '10px 14px', backgroundColor: '#eff6ff', borderRadius: '8px', fontSize: '13px', color: '#1e40af' }}>
                  {isRu
                    ? 'Ожидаемые столбцы: №, Фамилия, Имя, Отчество, Дата рождения, Возраст, Город, Место работы, Отдел, Должность, Отношение, Пол'
                    : "Kutilayotgan ustunlar: №, Familiya, Ism, Otasining ismi, Tug'ilgan sana, Yoshi, Shahar, Ish joyi, Bo'lim, Lavozim, Munosabat, Jinsi"}
                </div>

                <button
                  style={{ ...s.btn, ...s.btnPrimary, width: '100%', justifyContent: 'center', marginTop: '16px' }}
                  onClick={handleUploadPreview}
                  disabled={!uploadFile || uploadLoading}
                >
                  {uploadLoading ? <RefreshCw size={16} /> : <Upload size={16} />}
                  {isRu ? 'Загрузить и проверить' : 'Yuklash va tekshirish'}
                </button>
              </div>
            )}

            {/* Step 2: Preview */}
            {uploadStep === 'preview' && uploadPreview && (() => {
              const PAGE_SIZE = 50;
              const allEmps = uploadPreview.employees || [];
              const totalPages = Math.ceil(allEmps.length / PAGE_SIZE);
              const pageEmps = allEmps.slice((uploadPage - 1) * PAGE_SIZE, uploadPage * PAGE_SIZE);
              return (
              <div>
                {/* Summary */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ ...s.summaryCard, backgroundColor: '#f0fdf4', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#22c55e' }}>{uploadPreview.valid}</p>
                    <p style={{ ...s.summaryLabel, color: '#16a34a' }}>{isRu ? 'Валидных' : 'Yaroqli'}</p>
                  </div>
                  <div style={{ ...s.summaryCard, backgroundColor: '#fef2f2', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#ef4444' }}>{uploadPreview.with_errors}</p>
                    <p style={{ ...s.summaryLabel, color: '#dc2626' }}>{isRu ? 'С ошибками' : 'Xatoli'}</p>
                  </div>
                  <div style={{ ...s.summaryCard, backgroundColor: '#fff7ed', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#ea580c' }}>{uploadPreview.with_warnings || 0}</p>
                    <p style={{ ...s.summaryLabel, color: '#c2410c' }}>{isRu ? 'Без отдела' : 'Bo\'limsiz'}</p>
                  </div>
                  <div style={{ ...s.summaryCard, backgroundColor: '#fffbeb', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#f59e0b' }}>{uploadPreview.duplicates}</p>
                    <p style={{ ...s.summaryLabel, color: '#d97706' }}>{isRu ? 'Дубликаты' : 'Dublikatlar'}</p>
                  </div>
                  <div style={{ ...s.summaryCard, backgroundColor: '#f1f5f9', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#475569' }}>{uploadPreview.total_rows}</p>
                    <p style={{ ...s.summaryLabel, color: '#64748b' }}>{isRu ? 'Всего' : 'Jami'}</p>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto', marginBottom: '8px' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>№</th>
                        <th style={s.th}>{isRu ? 'Фамилия' : 'Familiya'}</th>
                        <th style={s.th}>{isRu ? 'Имя' : 'Ism'}</th>
                        <th style={s.th}>{isRu ? 'Отчество' : 'Otasining ismi'}</th>
                        <th style={s.th}>{isRu ? 'Отдел' : "Bo'lim"}</th>
                        <th style={s.th}>{isRu ? 'Должность' : 'Lavozim'}</th>
                        <th style={s.th}>{isRu ? 'Пол' : 'Jinsi'}</th>
                        <th style={s.th}>Username</th>
                        <th style={s.th}>{isRu ? 'Статус' : 'Holat'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageEmps.map((emp, idx) => {
                        const hasErrors = emp.errors && emp.errors.length > 0;
                        const hasWarnings = emp.warnings && emp.warnings.length > 0;
                        const bgColor = hasErrors ? '#fef2f2' : emp.is_duplicate ? '#fffbeb' : hasWarnings ? '#fff7ed' : (idx % 2 === 0 ? '#fff' : '#fafbfc');
                        return (
                          <tr key={idx} style={{ backgroundColor: bgColor }}>
                            <td style={s.td}>{emp.row}</td>
                            <td style={{ ...s.td, fontWeight: 500 }}>{emp.last_name}</td>
                            <td style={s.td}>{emp.first_name}</td>
                            <td style={s.td}>{emp.middle_name}</td>
                            <td style={s.td}>
                              {emp.department_matched || (
                                <span style={{ color: '#ea580c', fontSize: '12px' }}>
                                  {emp.department_name ? `⚠ ${emp.department_name}` : '—'}
                                </span>
                              )}
                            </td>
                            <td style={s.td}>{emp.position || '—'}</td>
                            <td style={s.td}>{emp.gender === 'M' ? (isRu ? 'М' : 'E') : emp.gender === 'F' ? (isRu ? 'Ж' : 'A') : '—'}</td>
                            <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '13px' }}>{emp.username}</td>
                            <td style={s.td}>
                              {hasErrors ? (
                                <span style={{ fontSize: '12px', color: '#ef4444' }}>
                                  {emp.errors.map(e => e.message).join('; ')}
                                </span>
                              ) : emp.is_duplicate ? (
                                <span style={s.badge('#d97706', '#fffbeb')}>
                                  {isRu ? 'Дубл.' : 'Dubl.'}
                                </span>
                              ) : (
                                <span style={s.badge('#22c55e', '#f0fdf4')}>OK</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                    <button
                      style={{ ...s.btn, ...s.btnSecondary, padding: '4px 10px', fontSize: '13px' }}
                      onClick={() => setUploadPage(p => Math.max(1, p - 1))}
                      disabled={uploadPage <= 1}
                    >&lt;</button>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      {isRu
                        ? `${(uploadPage-1)*PAGE_SIZE+1}–${Math.min(uploadPage*PAGE_SIZE, allEmps.length)} из ${allEmps.length}`
                        : `${(uploadPage-1)*PAGE_SIZE+1}–${Math.min(uploadPage*PAGE_SIZE, allEmps.length)} / ${allEmps.length}`}
                    </span>
                    <button
                      style={{ ...s.btn, ...s.btnSecondary, padding: '4px 10px', fontSize: '13px' }}
                      onClick={() => setUploadPage(p => Math.min(totalPages, p + 1))}
                      disabled={uploadPage >= totalPages}
                    >&gt;</button>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    style={{ ...s.btn, ...s.btnSecondary, flex: 1, justifyContent: 'center' }}
                    onClick={() => setUploadStep('select')}
                  >
                    {isRu ? 'Назад' : 'Orqaga'}
                  </button>
                  <button
                    style={{ ...s.btn, ...s.btnPrimary, flex: 1, justifyContent: 'center', fontSize: '15px', padding: '12px 16px' }}
                    onClick={handleUploadConfirm}
                    disabled={uploadLoading || uploadPreview.total_rows === 0}
                  >
                    {uploadLoading ? <RefreshCw size={16} /> : <CheckCircle size={16} />}
                    {isRu
                      ? `Импортировать ${uploadPreview.valid} сотр.`
                      : `${uploadPreview.valid} xodimni import qilish`}
                  </button>
                </div>
              </div>
              );
            })()}

            {/* Step 3: Result */}
            {uploadStep === 'result' && uploadResult && (
              <div>
                <div style={{ textAlign: 'center', padding: '20px 0 16px' }}>
                  <CheckCircle size={48} color="#22c55e" />
                  <h3 style={{ fontSize: '18px', color: '#1e293b', margin: '12px 0 4px' }}>
                    {isRu ? 'Импорт завершён!' : 'Import yakunlandi!'}
                  </h3>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ ...s.summaryCard, backgroundColor: '#f0fdf4', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#22c55e' }}>{uploadResult.created}</p>
                    <p style={{ ...s.summaryLabel, color: '#16a34a' }}>{isRu ? 'Создано' : 'Yaratildi'}</p>
                  </div>
                  <div style={{ ...s.summaryCard, backgroundColor: '#f1f5f9', flex: 1 }}>
                    <p style={{ ...s.summaryValue, color: '#64748b' }}>{uploadResult.skipped}</p>
                    <p style={{ ...s.summaryLabel, color: '#64748b' }}>{isRu ? 'Пропущено' : "O'tkazildi"}</p>
                  </div>
                  {uploadResult.deactivated > 0 && (
                    <div style={{ ...s.summaryCard, backgroundColor: '#fffbeb', flex: 1 }}>
                      <p style={{ ...s.summaryValue, color: '#d97706' }}>{uploadResult.deactivated}</p>
                      <p style={{ ...s.summaryLabel, color: '#d97706' }}>{isRu ? 'Деактивировано' : "O'chirildi"}</p>
                    </div>
                  )}
                </div>

                {uploadResult.created_users && uploadResult.created_users.length > 0 && (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px' }}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Username</th>
                          <th style={s.th}>{isRu ? 'ФИО' : 'F.I.O.'}</th>
                          <th style={s.th}>{isRu ? 'Отдел' : "Bo'lim"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.created_users.map((u, idx) => (
                          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <td style={{ ...s.td, fontFamily: 'monospace' }}>{u.username}</td>
                            <td style={s.td}>{u.full_name}</td>
                            <td style={s.td}>{u.department || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '8px', marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#dc2626', fontSize: '14px' }}>
                      {isRu ? 'Ошибки:' : 'Xatolar:'}
                    </p>
                    {uploadResult.errors.map((err, idx) => (
                      <p key={idx} style={{ margin: '4px 0', fontSize: '13px', color: '#dc2626' }}>
                        {isRu ? `Строка ${err.row}` : `${err.row}-qator`}: {err.error}
                      </p>
                    ))}
                  </div>
                )}

                <button
                  style={{ ...s.btn, ...s.btnPrimary, width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    setShowUploadModal(false);
                    loadEmployees();
                  }}
                >
                  {isRu ? 'Закрыть' : 'Yopish'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Employee Create/Edit Modal */}
      {showEmpForm && (
        <div style={s.modal} onClick={() => setShowEmpForm(false)}>
          <div style={{ ...s.modalContent, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingEmp ? <Edit3 size={18} color="#3b82f6" /> : <Plus size={18} color="#22c55e" />}
                {editingEmp
                  ? (isRu ? 'Редактировать сотрудника' : 'Xodimni tahrirlash')
                  : (isRu ? 'Добавить сотрудника' : 'Xodim qo\'shish')}
              </span>
              <button style={{ ...s.btn, ...s.btnSecondary, padding: '4px 8px' }} onClick={() => setShowEmpForm(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Фамилия *' : 'Familiya *'}</label>
                <input style={s.input} value={empForm.last_name} onChange={e => setEmpForm(p => ({ ...p, last_name: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Имя *' : 'Ism *'}</label>
                <input style={s.input} value={empForm.first_name} onChange={e => setEmpForm(p => ({ ...p, first_name: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Отчество' : 'Otasining ismi'}</label>
                <input style={s.input} value={empForm.middle_name} onChange={e => setEmpForm(p => ({ ...p, middle_name: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Отдел' : "Bo'lim"}</label>
                <select style={s.select} value={empForm.department} onChange={e => setEmpForm(p => ({ ...p, department: e.target.value }))}>
                  <option value="">{isRu ? '— Выберите —' : '— Tanlang —'}</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{isRu ? d.name_ru : d.name_uz}</option>
                  ))}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Должность' : 'Lavozim'}</label>
                <input style={s.input} value={empForm.position} onChange={e => setEmpForm(p => ({ ...p, position: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Роль' : 'Rol'}</label>
                <select style={s.select} value={empForm.role} onChange={e => setEmpForm(p => ({ ...p, role: e.target.value }))}>
                  <option value="employee">{isRu ? 'Сотрудник' : 'Xodim'}</option>
                  <option value="department_head">{isRu ? 'Нач. отдела' : "Bo'lim boshlig'i"}</option>
                  <option value="director">{isRu ? 'Директор' : 'Direktor'}</option>
                  <option value="deputy_director">{isRu ? 'Зам. директора' : 'Direktor o\'rinbosari'}</option>
                  <option value="analyst">{isRu ? 'Аналитик' : 'Tahlilchi'}</option>
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Пол' : 'Jinsi'}</label>
                <select style={s.select} value={empForm.gender} onChange={e => setEmpForm(p => ({ ...p, gender: e.target.value }))}>
                  <option value="">—</option>
                  <option value="M">{isRu ? 'Мужской' : 'Erkak'}</option>
                  <option value="F">{isRu ? 'Женский' : 'Ayol'}</option>
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Дата рождения' : "Tug'ilgan sana"}</label>
                <input style={s.input} type="date" value={empForm.birth_date} onChange={e => setEmpForm(p => ({ ...p, birth_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Дата приёма' : 'Qabul sanasi'}</label>
                <input style={s.input} type="date" value={empForm.hire_date} onChange={e => setEmpForm(p => ({ ...p, hire_date: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>{isRu ? 'Телефон' : 'Telefon'}</label>
                <input style={s.input} value={empForm.phone} onChange={e => setEmpForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Email</label>
                <input style={s.input} type="email" value={empForm.email} onChange={e => setEmpForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              {!editingEmp && (
                <div style={s.formGroup}>
                  <label style={s.label}>{isRu ? 'Пароль' : 'Parol'}</label>
                  <input style={s.input} value={empForm.password} onChange={e => setEmpForm(p => ({ ...p, password: e.target.value }))} />
                </div>
              )}
            </div>

            <button
              style={{ ...s.btn, ...s.btnPrimary, width: '100%', justifyContent: 'center', marginTop: '16px' }}
              onClick={handleSaveEmployee}
              disabled={empSaving}
            >
              {empSaving ? <RefreshCw size={16} /> : <Save size={16} />}
              {editingEmp
                ? (isRu ? 'Сохранить изменения' : 'O\'zgarishlarni saqlash')
                : (isRu ? 'Создать сотрудника' : 'Xodim yaratish')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Attendance Tab ────────────────────────────────────────────────

  function renderAttendance() {
    // Считаем сводку из attendanceMap (совпадает с таблицей)
    const localTotals = {};
    ATTENDANCE_STATUSES.forEach(st => { localTotals[st.value] = 0; });
    Object.values(attendanceMap).forEach(a => {
      localTotals[a.status] = (localTotals[a.status] || 0) + 1;
    });

    // Количество отделов, которые уже сохранили посещаемость в БД
    const reportedDepts = summary?.departments?.length || 0;
    const totalDepts = departments.length;

    return (
      <div>
        {/* Controls */}
        <div style={s.controlRow}>
          <Calendar size={16} color="#64748b" />
          <input
            type="date"
            style={s.dateInput}
            value={attDate}
            onChange={e => setAttDate(e.target.value)}
          />
          <select style={s.select} value={attDeptFilter} onChange={e => setAttDeptFilter(e.target.value)}>
            <option value="">{isRu ? 'Все отделы' : "Barcha bo'limlar"}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{isRu ? d.name_ru : d.name_uz}</option>
            ))}
          </select>
          <button style={{ ...s.btn, ...s.btnSecondary }} onClick={loadAttendance}>
            <RefreshCw size={14} /> {isRu ? 'Обновить' : 'Yangilash'}
          </button>
          <button
            style={{ ...s.btn, ...s.btnSuccess, marginLeft: 'auto' }}
            onClick={saveAttendance}
            disabled={attSaving || employees.length === 0}
          >
            {attSaving ? <RefreshCw size={16} /> : <Save size={16} />}
            {isRu ? 'Сохранить посещаемость' : 'Davomatni saqlash'}
          </button>

          {/* Кнопки подтверждения HR (только когда выбран конкретный отдел) */}
          {attDeptFilter && (
            confirmations[attDeptFilter] ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', color: '#16a34a', fontSize: '13px', fontWeight: 600 }}>
                  <ShieldCheck size={15} />
                  {isRu ? 'Подтверждено HR' : 'HR tasdiqlagan'}
                </span>
                <button
                  style={{ ...s.btn, ...s.btnSecondary, fontSize: '13px' }}
                  onClick={() => handleUnconfirm(attDeptFilter)}
                  disabled={confirmLoading}
                  title={isRu ? 'Снять подтверждение' : 'Tasdiqlashni bekor qilish'}
                >
                  <Unlock size={14} /> {isRu ? 'Снять' : 'Bekor'}
                </button>
              </div>
            ) : (
              <button
                style={{ ...s.btn, backgroundColor: '#7c3aed', color: '#fff' }}
                onClick={() => handleConfirm(attDeptFilter)}
                disabled={confirmLoading || employees.length === 0}
              >
                <Lock size={14} />
                {isRu ? 'Подтвердить посещаемость' : 'Davomatni tasdiqlash'}
              </button>
            )
          )}
        </div>

        {attMessage && (
          <div style={{ padding: '10px 16px', marginBottom: '16px', borderRadius: '8px', backgroundColor: attMessage.includes('Ошибка') || attMessage.includes('xatolik') ? '#fef2f2' : '#f0fdf4', color: attMessage.includes('Ошибка') || attMessage.includes('xatolik') ? '#dc2626' : '#16a34a', fontSize: '14px' }}>
            {attMessage}
          </div>
        )}

        {/* Инфо: сколько отделов отчитались */}
        {!attDeptFilter && totalDepts > 0 && (
          <div style={{
            padding: '10px 16px', marginBottom: '16px', borderRadius: '8px',
            backgroundColor: reportedDepts >= totalDepts ? '#f0fdf4' : '#fffbeb',
            color: reportedDepts >= totalDepts ? '#16a34a' : '#92400e',
            fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <Users size={16} />
            {isRu
              ? `Отчитались: ${reportedDepts} из ${totalDepts} отделов`
              : `Hisobot berdi: ${totalDepts} dan ${reportedDepts} bo'lim`}
            {reportedDepts < totalDepts && (
              <span style={{ marginLeft: '8px', fontWeight: 500 }}>
                {isRu ? '(не отмеченные отображаются серым)' : "(belgilanmaganlar kulrang rangda ko'rsatilgan)"}
              </span>
            )}
          </div>
        )}

        {/* Сводка подтверждений по отделам (когда не выбран фильтр) */}
        {!attDeptFilter && departments.length > 0 && (() => {
          const confirmedCount = Object.keys(confirmations).length;
          const totalDepts = departments.length;
          return (
            <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', backgroundColor: confirmedCount === totalDepts ? '#f0fdf4' : '#faf5ff', border: `1px solid ${confirmedCount === totalDepts ? '#86efac' : '#ddd6fe'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, color: confirmedCount === totalDepts ? '#16a34a' : '#6d28d9' }}>
                  <ShieldCheck size={16} />
                  {isRu
                    ? `Подтверждено HR: ${confirmedCount} из ${totalDepts} отделов`
                    : `HR tomonidan tasdiqlangan: ${totalDepts} dan ${confirmedCount} bo'lim`}
                </span>
                {confirmedCount > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {departments
                      .filter(d => confirmations[d.id])
                      .map(d => (
                        <span key={d.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac' }}>
                          <Lock size={11} /> {isRu ? d.name_ru : d.name_uz}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Summary Cards — считаем из attendanceMap (всех сотрудников) */}
        {employees.length > 0 && (
          <div style={s.summaryRow}>
            {ATTENDANCE_STATUSES.map(st => (
              <div key={st.value} style={{ ...s.summaryCard, backgroundColor: st.bg }}>
                <p style={{ ...s.summaryValue, color: st.color }}>{localTotals[st.value] || 0}</p>
                <p style={{ ...s.summaryLabel, color: st.color }}>{isRu ? st.label_ru : st.label_uz}</p>
              </div>
            ))}
          </div>
        )}

        {/* Attendance Table */}
        <div style={{ ...s.card, ...(attDeptFilter && confirmations[attDeptFilter] ? { borderColor: '#86efac', boxShadow: '0 0 0 2px #dcfce7' } : {}) }}>
          <h3 style={s.cardTitle}>
            <Users size={18} />
            {isRu ? `Сотрудники (${employees.length})` : `Xodimlar (${employees.length})`}
            {attDeptFilter && confirmations[attDeptFilter] && (
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#16a34a', backgroundColor: '#f0fdf4', padding: '3px 10px', borderRadius: '20px', border: '1px solid #86efac' }}>
                <ShieldCheck size={13} />
                {isRu ? 'HR подтвердил — данные актуальны' : "HR tasdiqladi — ma'lumotlar dolzarb"}
              </span>
            )}
          </h3>

          {attLoading ? (
            <div style={s.loading}>{isRu ? 'Загрузка...' : 'Yuklanmoqda...'}</div>
          ) : employees.length === 0 ? (
            <div style={s.empty}>{isRu ? 'Нет сотрудников' : 'Xodimlar topilmadi'}</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>№</th>
                  <th style={s.th}>{isRu ? 'ФИО' : 'F.I.O.'}</th>
                  <th style={s.th}>{isRu ? 'Отдел' : "Bo'lim"}</th>
                  <th style={s.th}>{isRu ? 'Должность' : 'Lavozim'}</th>
                  <th style={{ ...s.th, width: '200px' }}>{isRu ? 'Статус' : 'Holat'}</th>
                  <th style={s.th}>{isRu ? 'Примечание' : 'Izoh'}</th>
                  <th style={s.th}>{isRu ? 'Объяснительная' : 'Tushuntirish'}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => {
                  const rec = attendanceMap[emp.id] || { status: 'present', note: '' };
                  const statusInfo = getStatusInfo(rec.status);
                  return (
                    <tr key={emp.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={s.td}>{idx + 1}</td>
                      <td style={{ ...s.td, fontWeight: '500' }}>{emp.full_name}</td>
                      <td style={s.td}>{isRu ? emp.department_name : emp.department_name_uz}</td>
                      <td style={s.td}>{emp.position || '—'}</td>
                      <td style={s.td}>
                        <select
                          style={{ ...s.statusSelect, borderColor: statusInfo.color, color: statusInfo.color }}
                          value={rec.status}
                          onChange={e => handleStatusChange(emp.id, e.target.value)}
                        >
                          {ATTENDANCE_STATUSES.map(st => (
                            <option key={st.value} value={st.value}>
                              {isRu ? st.label_ru : st.label_uz}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={s.td}>
                        <input
                          style={{ ...s.input, minWidth: '100px', flex: 'none', fontSize: '13px', padding: '4px 8px' }}
                          value={rec.note}
                          onChange={e => handleNoteChange(emp.id, e.target.value)}
                          placeholder={isRu ? 'Заметка' : 'Izoh'}
                        />
                      </td>
                      <td style={s.td}>
                        {(rec.status === 'late_excused' || rec.status === 'late_unexcused') && rec.id ? (
                          <button
                            style={{ ...s.btn, padding: '3px 8px', fontSize: '12px', background: '#fef3c7', color: '#d97706', border: '1px solid #fcd34d', borderRadius: '6px', cursor: 'pointer' }}
                            onClick={() => handleRequestExplanation(emp, attDate, rec.id)}
                            title={isRu ? 'Запросить объяснительную' : "Tushuntirish so'rash"}
                          >
                            <AlertCircle size={12} /> {isRu ? 'Запросить' : "So'rash"}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ─── Employees Tab ─────────────────────────────────────────────────

  function renderEmployees() {
    // Пагинация
    const empTotal = empList.length;
    const empTotalPages = empPageSize === 0 ? 1 : Math.ceil(empTotal / empPageSize);
    const empDisplayed = empPageSize === 0 ? empList : empList.slice((empPage - 1) * empPageSize, empPage * empPageSize);
    const empPages = buildPageNumbers(empPage, empTotalPages);

    const paginationBtnStyle = (active) => ({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: '32px', height: '32px', padding: '0 6px',
      border: active ? '1px solid #3b82f6' : '1px solid #e2e8f0',
      borderRadius: '6px', background: active ? '#3b82f6' : '#fff',
      color: active ? '#fff' : '#475569', fontSize: '13px', cursor: 'pointer',
      fontWeight: active ? '600' : '400', transition: 'all 0.15s',
    });
    const paginationBtnDisabled = { ...paginationBtnStyle(false), opacity: 0.4, cursor: 'not-allowed' };

    return (
      <div>
        {/* Filters */}
        <div style={s.controlRow}>
          <Search size={16} color="#64748b" />
          <input
            style={s.input}
            placeholder={isRu ? 'Поиск по ФИО...' : "F.I.O. bo'yicha qidirish..."}
            value={empSearch}
            onChange={e => setEmpSearch(e.target.value)}
          />
          <select style={s.select} value={empDeptFilter} onChange={e => setEmpDeptFilter(e.target.value)}>
            <option value="">{isRu ? 'Все отделы' : "Barcha bo'limlar"}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{isRu ? d.name_ru : d.name_uz}</option>
            ))}
          </select>
          <select style={s.select} value={empRoleFilter} onChange={e => setEmpRoleFilter(e.target.value)}>
            <option value="">{isRu ? 'Все роли' : 'Barcha rollar'}</option>
            <option value="director">{isRu ? 'Директор' : 'Direktor'}</option>
            <option value="deputy_director">{isRu ? 'Зам. директора' : 'Direktor o\'rinbosari'}</option>
            <option value="department_head">{isRu ? 'Нач. отдела' : "Bo'lim boshlig'i"}</option>
            <option value="employee">{isRu ? 'Сотрудник' : 'Xodim'}</option>
            <option value="analyst">{isRu ? 'Аналитик' : 'Analitik'}</option>
          </select>
          <button
            style={{ ...s.btn, ...s.btnPrimary, marginLeft: 'auto' }}
            onClick={openEmpCreate}
          >
            <Plus size={16} /> {isRu ? 'Добавить' : 'Qo\'shish'}
          </button>
          <button
            style={{ ...s.btn, ...s.btnSuccess }}
            onClick={() => {
              setShowUploadModal(true);
              setUploadStep('select');
              setUploadFile(null);
              setUploadPreview(null);
              setUploadResult(null);
            }}
          >
            <Upload size={16} /> {isRu ? 'Импорт Excel' : 'Excel import'}
          </button>
        </div>

        {/* Панель «Показать по» */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', fontSize: '14px', color: '#475569' }}>
          <span>{isRu ? 'Показать:' : "Ko'rsatish:"}</span>
          <select
            style={{ padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', color: '#475569', background: '#fff', cursor: 'pointer' }}
            value={empPageSize}
            onChange={e => { setEmpPageSize(Number(e.target.value)); setEmpPage(1); }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={0}>{isRu ? 'Все' : 'Hammasi'}</option>
          </select>
          <span style={{ color: '#94a3b8', marginLeft: '4px' }}>
            {isRu ? `Найдено: ${empTotal}` : `Topildi: ${empTotal}`}
          </span>
        </div>

        {/* Table */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>
            <Users size={18} />
            {isRu ? `Список сотрудников (${empTotal})` : `Xodimlar ro'yxati (${empTotal})`}
          </h3>

          {empLoading ? (
            <div style={s.loading}>{isRu ? 'Загрузка...' : 'Yuklanmoqda...'}</div>
          ) : empList.length === 0 ? (
            <div style={s.empty}>{isRu ? 'Нет сотрудников' : 'Xodimlar topilmadi'}</div>
          ) : (
            <>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>№</th>
                    <th style={s.th}>{isRu ? 'ФИО' : 'F.I.O.'}</th>
                    <th style={s.th}>{isRu ? 'Отдел' : "Bo'lim"}</th>
                    <th style={s.th}>{isRu ? 'Должность' : 'Lavozim'}</th>
                    <th style={s.th}>{isRu ? 'Дата приёма' : 'Qabul sanasi'}</th>
                    <th style={s.th}>{isRu ? 'Телефон' : 'Telefon'}</th>
                    <th style={s.th}>{isRu ? 'Действия' : 'Amallar'}</th>
                  </tr>
                </thead>
                <tbody>
                  {empDisplayed.map((emp, idx) => (
                    <tr
                      key={emp.id}
                      style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}
                    >
                      <td style={s.td}>{empPageSize === 0 ? idx + 1 : (empPage - 1) * empPageSize + idx + 1}</td>
                      <td
                        style={{ ...s.td, fontWeight: '500', color: '#2563eb', cursor: 'pointer' }}
                        onClick={() => openEmployeeDetail(emp.id)}
                      >{emp.full_name}</td>
                      <td style={s.td}>{isRu ? emp.department_name : emp.department_name_uz}</td>
                      <td style={s.td}>{emp.position || '—'}</td>
                      <td style={s.td}>{formatDate(emp.hire_date)}</td>
                      <td style={s.td}>{emp.phone || '—'}</td>
                      <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                        <button
                          style={{ ...s.btn, ...s.btnSecondary, padding: '3px 8px', fontSize: '12px', marginRight: '4px' }}
                          onClick={() => openEmpEdit(emp)}
                          title={isRu ? 'Редактировать' : 'Tahrirlash'}
                        ><Edit3 size={13} /></button>
                        <button
                          style={{ ...s.btn, padding: '3px 8px', fontSize: '12px', color: '#ef4444', border: '1px solid #fecaca', backgroundColor: '#fff' }}
                          onClick={() => handleDeleteEmployee(emp)}
                          title={isRu ? 'Деактивировать' : 'O\'chirish'}
                        ><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Пагинация */}
              {empTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '12px 0', borderTop: '1px solid #f1f5f9' }}>
                  <button style={empPage <= 1 ? paginationBtnDisabled : paginationBtnStyle(false)} disabled={empPage <= 1} onClick={() => setEmpPage(1)}>
                    <ChevronsLeft size={16} />
                  </button>
                  <button style={empPage <= 1 ? paginationBtnDisabled : paginationBtnStyle(false)} disabled={empPage <= 1} onClick={() => setEmpPage(p => p - 1)}>
                    <ChevronLeft size={16} />
                  </button>
                  {empPages.map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '24px', height: '32px', color: '#94a3b8', fontSize: '13px' }}>...</span>
                    ) : (
                      <button key={p} style={paginationBtnStyle(p === empPage)} onClick={() => setEmpPage(p)}>
                        {p}
                      </button>
                    )
                  )}
                  <button style={empPage >= empTotalPages ? paginationBtnDisabled : paginationBtnStyle(false)} disabled={empPage >= empTotalPages} onClick={() => setEmpPage(p => p + 1)}>
                    <ChevronRight size={16} />
                  </button>
                  <button style={empPage >= empTotalPages ? paginationBtnDisabled : paginationBtnStyle(false)} disabled={empPage >= empTotalPages} onClick={() => setEmpPage(empTotalPages)}>
                    <ChevronsRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Transfers Tab ─────────────────────────────────────────────────

  function renderExplanations() {
    const statusColors = {
      requested: { color: '#d97706', bg: '#fffbeb', label_ru: 'Запрошена', label_uz: 'So\'raldi' },
      submitted:  { color: '#2563eb', bg: '#eff6ff', label_ru: 'Представлена', label_uz: 'Topshirildi' },
      reviewed:   { color: '#16a34a', bg: '#f0fdf4', label_ru: 'Рассмотрена', label_uz: 'Ko\'rib chiqildi' },
    };

    return (
      <div>
        <div style={s.controlRow}>
          <select
            style={{ ...s.select, minWidth: '180px' }}
            value={expFilter}
            onChange={e => setExpFilter(e.target.value)}
          >
            <option value="">{isRu ? 'Все статусы' : 'Barcha holat'}</option>
            <option value="requested">{isRu ? 'Запрошена' : "So'raldi"}</option>
            <option value="submitted">{isRu ? 'Представлена' : 'Topshirildi'}</option>
            <option value="reviewed">{isRu ? 'Рассмотрена' : "Ko'rib chiqildi"}</option>
          </select>
          <button style={{ ...s.btn, ...s.btnSecondary }} onClick={loadExplanations}>
            <RefreshCw size={14} /> {isRu ? 'Обновить' : 'Yangilash'}
          </button>
        </div>

        <div style={s.card}>
          <h3 style={s.cardTitle}>
            <FileText size={18} />
            {isRu ? `Объяснительные (${explanations.length})` : `Tushuntirish xatlari (${explanations.length})`}
          </h3>

          {expLoading ? (
            <div style={s.loading}>{isRu ? 'Загрузка...' : 'Yuklanmoqda...'}</div>
          ) : explanations.length === 0 ? (
            <div style={s.empty}>{isRu ? 'Нет объяснительных' : 'Tushuntirish xatlari topilmadi'}</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{isRu ? 'Дата' : 'Sana'}</th>
                  <th style={s.th}>{isRu ? 'Сотрудник' : 'Xodim'}</th>
                  <th style={s.th}>{isRu ? 'Отдел' : "Bo'lim"}</th>
                  <th style={s.th}>{isRu ? 'Статус посещаемости' : 'Davomat holati'}</th>
                  <th style={s.th}>{isRu ? 'Статус' : 'Holat'}</th>
                  <th style={s.th}>{isRu ? 'Запросил' : "So'ragan"}</th>
                  <th style={s.th}>{isRu ? 'Файл' : 'Fayl'}</th>
                  <th style={s.th}>{isRu ? 'Действия' : 'Amallar'}</th>
                </tr>
              </thead>
              <tbody>
                {explanations.map((exp, idx) => {
                  const sc = statusColors[exp.status] || {};
                  return (
                    <tr key={exp.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={s.td}>{formatDate(exp.date)}</td>
                      <td style={{ ...s.td, fontWeight: '500' }}>{exp.employee_name}</td>
                      <td style={s.td}>{isRu ? exp.employee_department : (exp.employee_department_uz || exp.employee_department)}</td>
                      <td style={s.td}>{exp.attendance_status_display || '—'}</td>
                      <td style={s.td}>
                        <span style={{ background: sc.bg, color: sc.color, padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
                          {isRu ? sc.label_ru : sc.label_uz}
                        </span>
                      </td>
                      <td style={s.td}>{exp.requested_by_name || '—'}</td>
                      <td style={s.td}>
                        {exp.has_file ? (
                          <button
                            style={{ ...s.btn, ...s.btnSecondary, padding: '3px 8px', fontSize: '12px' }}
                            onClick={() => handleDownloadExplanation(exp)}
                          >
                            <Download size={13} /> PDF
                          </button>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>{isRu ? 'Не загружен' : 'Yuklanmagan'}</span>
                        )}
                      </td>
                      <td style={s.td}>
                        {exp.status === 'submitted' && (
                          expReviewing === exp.id ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <input
                                style={{ ...s.input, fontSize: '12px', padding: '4px 6px' }}
                                placeholder={isRu ? 'Примечание HR...' : 'HR izohi...'}
                                value={expReviewNote}
                                onChange={e => setExpReviewNote(e.target.value)}
                              />
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  style={{ ...s.btn, ...s.btnPrimary, padding: '3px 8px', fontSize: '12px' }}
                                  onClick={() => handleReviewExplanation(exp.id)}
                                >
                                  <CheckCircle size={12} /> {isRu ? 'Сохранить' : 'Saqlash'}
                                </button>
                                <button
                                  style={{ ...s.btn, ...s.btnSecondary, padding: '3px 8px', fontSize: '12px' }}
                                  onClick={() => { setExpReviewing(null); setExpReviewNote(''); }}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              style={{ ...s.btn, ...s.btnPrimary, padding: '3px 8px', fontSize: '12px' }}
                              onClick={() => { setExpReviewing(exp.id); setExpReviewNote(exp.hr_note || ''); }}
                            >
                              <CheckCircle size={13} /> {isRu ? 'Рассмотреть' : "Ko'rib chiqish"}
                            </button>
                          )
                        )}
                        {exp.status === 'reviewed' && exp.hr_note && (
                          <span style={{ color: '#64748b', fontSize: '12px' }}>📝 {exp.hr_note}</span>
                        )}
                        {exp.status === 'requested' && (
                          <span style={{ color: '#94a3b8', fontSize: '12px' }}>
                            <AlertCircle size={12} style={{ verticalAlign: 'middle' }} /> {isRu ? 'Ожидание' : 'Kutilmoqda'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  function renderTransfers() {
    return (
      <div>
        <div style={s.controlRow}>
          <button style={{ ...s.btn, ...s.btnPrimary }} onClick={() => { loadEmployees(); setShowTransferModal(true); }}>
            <ArrowRightLeft size={16} /> {isRu ? 'Оформить перевод' : "Ko'chirishni rasmiylashtirish"}
          </button>
          <button style={{ ...s.btn, ...s.btnSecondary }} onClick={loadTransfers}>
            <RefreshCw size={14} /> {isRu ? 'Обновить' : 'Yangilash'}
          </button>
        </div>

        <div style={s.card}>
          <h3 style={s.cardTitle}>
            <ArrowRightLeft size={18} />
            {isRu ? `История переводов (${transfers.length})` : `Ko'chirishlar tarixi (${transfers.length})`}
          </h3>

          {trLoading ? (
            <div style={s.loading}>{isRu ? 'Загрузка...' : 'Yuklanmoqda...'}</div>
          ) : transfers.length === 0 ? (
            <div style={s.empty}>{isRu ? 'Нет переводов' : "Ko'chirishlar topilmadi"}</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{isRu ? 'Дата' : 'Sana'}</th>
                  <th style={s.th}>{isRu ? 'Сотрудник' : 'Xodim'}</th>
                  <th style={s.th}>{isRu ? 'Из отдела' : "Qaysi bo'limdan"}</th>
                  <th style={s.th}>{isRu ? 'В отдел' : "Qaysi bo'limga"}</th>
                  <th style={s.th}>{isRu ? 'Причина' : 'Sabab'}</th>
                  <th style={s.th}>{isRu ? '№ приказа' : 'Buyruq №'}</th>
                  <th style={s.th}>{isRu ? 'Оформил' : 'Rasmiylashtirdi'}</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((tr, idx) => (
                  <tr key={tr.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={s.td}>{formatDate(tr.transfer_date)}</td>
                    <td style={{ ...s.td, fontWeight: '500' }}>{tr.employee_name}</td>
                    <td style={s.td}>{isRu ? tr.from_department_name : (tr.from_department_name_uz || tr.from_department_name) || '—'}</td>
                    <td style={s.td}>{isRu ? tr.to_department_name : (tr.to_department_name_uz || tr.to_department_name)}</td>
                    <td style={s.td}>{tr.reason || '—'}</td>
                    <td style={s.td}>{tr.order_number || '—'}</td>
                    <td style={s.td}>{tr.created_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
}

export default HRDashboard;
